import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { useAccount } from 'wagmi';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { config } from '../utils/config';

const VESTING_CONTRACT_ADDRESS = config.ink.vestingContractAddress;

const VESTING_ABI = [
  {
    "inputs": [
      {"internalType": "address", "name": "_user", "type": "address"},
      {"internalType": "address", "name": "_tokenAddress", "type": "address"}
    ],
    "name": "getActiveVestings",
    "outputs": [
      {"internalType": "uint256[]", "name": "vestingIds", "type": "uint256[]"},
      {"internalType": "uint256[]", "name": "totalAmounts", "type": "uint256[]"},
      {"internalType": "uint256[]", "name": "claimedAmounts", "type": "uint256[]"},
      {"internalType": "uint256[]", "name": "claimableAmounts", "type": "uint256[]"},
      {"internalType": "uint256[]", "name": "endTimes", "type": "uint256[]"}
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

const ERC20_ABI = [
  "function decimals() view returns (uint8)"
];

export interface VestingSchedule {
  vestingId: bigint;
  totalAmount: bigint;
  claimedAmount: bigint;
  claimableAmount: bigint;
  endTime: bigint;
}

export interface UseVestingSchedulesResult {
  schedules: VestingSchedule[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useVestingSchedules(tokenAddress: string | null): UseVestingSchedulesResult {
  const { address } = useAccount();
  const [schedules, setSchedules] = useState<VestingSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = async () => {
    if (!tokenAddress || !address) {
      setSchedules([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await executeWithRpcFailover(async (provider) => {
        const vestingContract = new ethers.Contract(VESTING_CONTRACT_ADDRESS, VESTING_ABI, provider);
        
        const result = await vestingContract.getActiveVestings(address, tokenAddress);
        
        const [vestingIds, totalAmounts, claimedAmounts, claimableAmounts, endTimes] = result;

        const schedulesData: VestingSchedule[] = vestingIds.map((id: bigint, index: number) => ({
          vestingId: id,
          totalAmount: totalAmounts[index],
          claimedAmount: claimedAmounts[index],
          claimableAmount: claimableAmounts[index],
          endTime: endTimes[index]
        }));

        setSchedules(schedulesData);
      });
    } catch (err) {
      console.error('Error fetching vesting schedules:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch vesting schedules');
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [tokenAddress, address]);

  return {
    schedules,
    isLoading,
    error,
    refetch: fetchSchedules
  };
}

